import type { Workflow } from '@/stores/workflowStore';
import { getSmartBlockInputRequirements } from '@/services/blockAutoConfigure';

export interface SimulatedFlowTrace {
  reachableNodeIds: string[];
  blockedNodeIds: string[];
  failpointNodeIds: string[];
  predictedComplete: boolean;
  reasons: string[];
}

export function simulateFlowTrace(workflow: Workflow): SimulatedFlowTrace {
  const nodeIds = new Set(workflow.nodes.map((n) => n.id));
  const outgoing = new Map<string, string[]>();
  const incoming = new Map<string, string[]>();
  workflow.nodes.forEach((n) => {
    outgoing.set(n.id, []);
    incoming.set(n.id, []);
  });
  workflow.edges.forEach((e) => {
    if (!nodeIds.has(e.from) || !nodeIds.has(e.to)) return;
    outgoing.get(e.from)?.push(e.to);
    incoming.get(e.to)?.push(e.from);
  });

  const blockedSet = new Set(
    workflow.nodes
      .filter((n) => getSmartBlockInputRequirements(n).length > 0)
      .map((n) => n.id)
  );
  const startIds = workflow.nodes.filter((n) => n.type === 'start').map((n) => n.id);
  const reachable = new Set<string>();
  const queue = [...startIds];
  const reasons: string[] = [];

  while (queue.length > 0) {
    const id = queue.shift()!;
    if (reachable.has(id)) continue;
    reachable.add(id);
    if (blockedSet.has(id)) {
      reasons.push('A block requires human input before simulation can continue.');
      continue;
    }
    for (const next of outgoing.get(id) ?? []) {
      if (!reachable.has(next)) queue.push(next);
    }
  }

  const disconnected = workflow.nodes
    .filter((n) => n.type !== 'start' && !reachable.has(n.id))
    .map((n) => n.id);
  const disconnectedSet = new Set(disconnected);
  const failpoints = disconnected.filter((id) =>
    (incoming.get(id) ?? []).every((parent) => !disconnectedSet.has(parent))
  );
  if (failpoints.length > 0) {
    reasons.push('Disconnected failpoints prevent full path execution.');
  }

  const goal = workflow.nodes.find((n) => n.type === 'goal' && n.subtype === 'outcome');
  const predictedComplete = Boolean(goal && reachable.has(goal.id) && !blockedSet.has(goal.id));
  if (!predictedComplete) {
    reasons.push('Simulation did not reach GOAL.');
  }

  return {
    reachableNodeIds: [...reachable],
    blockedNodeIds: [...blockedSet],
    failpointNodeIds: failpoints,
    predictedComplete,
    reasons,
  };
}
